import i18n from '@/utils/i18n';
import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTableColumnHeader';
import { centsToPrice } from '@/utils/formatter';
import { Currency, DEFAULT_CURRENCY } from '@/utils/price';

export type MwkEventOverviewRow = {
  eventId: string;
  eventName: string;
  /** ISO code or C# enum name; may be missing or wrongly typed in JSON. */
  currency?: string | number;
  containerTypeId: number;
  containerTypeName: string;
  packedAfterCleaning: number;
  amountsFullBoxes: number;
  leftOverAmounts?: number;
  containerAmounts: number;
  picking?: number;
  returnedSealed?: number;
  countedBoxes?: number;
  logisticPartnerCleaningPrice?: number;
};

export function getMwkEventOverviewRowId(row: MwkEventOverviewRow): string {
  return `${row.eventId}-${row.containerTypeId}`;
}

/**
 * Normalized ISO currency for display and totals (same rules as centsToPrice).
 * Never call string methods on `currency` directly — API may send numbers or null.
 */
export function resolvedMwkEventOverviewCurrency(
  row: MwkEventOverviewRow,
): string {
  if (row.currency == null || row.currency === '') {
    return DEFAULT_CURRENCY;
  }
  const normalized = String(row.currency).trim();
  if (!normalized) {
    return DEFAULT_CURRENCY;
  }
  const upper = normalized.toUpperCase();
  return Currency[upper as keyof typeof Currency] ?? DEFAULT_CURRENCY;
}

export const columns: ColumnDef<MwkEventOverviewRow>[] = [
  {
    accessorKey: 'eventName',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:event')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:min-w-[150px]">{row.getValue('eventName')}</div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:event'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'containerTypeId',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:containerTypeId')}
      />
    ),
    cell: ({ row }) => <div>{row.getValue('containerTypeId')}</div>,
    meta: {
      getName: () => i18n.t('EventOverview:containerTypeId'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'containerTypeName',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:containerTypeName')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:min-w-[120px]">
        {row.getValue('containerTypeName')}
      </div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:containerTypeName'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'packedAfterCleaning',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:packedAfterCleaning')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">{row.getValue('packedAfterCleaning')}</div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:packedAfterCleaning'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'amountsFullBoxes',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:amountsFullBoxes')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">{row.getValue('amountsFullBoxes')}</div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:amountsFullBoxes'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'leftOverAmounts',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:leftOverAmounts')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">
        {row.getValue('leftOverAmounts') ?? 0}
      </div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:leftOverAmounts'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'containerAmounts',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:containerAmounts')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">{row.getValue('containerAmounts')}</div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:containerAmounts'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'picking',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:picking')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">{row.getValue('picking') ?? 0}</div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:picking'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'returnedSealed',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:returnedSealed')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">{row.getValue('returnedSealed') ?? 0}</div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:returnedSealed'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'countedBoxes',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:countedBoxes')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">{row.getValue('countedBoxes') ?? 0}</div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:countedBoxes'),
    },
    enableSorting: false,
  },
  {
    accessorKey: 'logisticPartnerCleaningPrice',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={i18n.t('EventOverview:logisticPartnerCleaningPrice')}
      />
    ),
    cell: ({ row }) => (
      <div className="tw:text-right">
        {centsToPrice(
          row.getValue('logisticPartnerCleaningPrice') ?? 0,
          resolvedMwkEventOverviewCurrency(row.original),
        )}
      </div>
    ),
    meta: {
      getName: () => i18n.t('EventOverview:logisticPartnerCleaningPrice'),
    },
    enableSorting: false,
  },
];
